import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors, fonts, sizes, spacing, radius } from '../styles/designTokens';
import FormInput from '../components/common/FormInput';
import SearchableDropdown from '../components/common/SearchableDropdown';
// OLD IMPLEMENTATION (Manual State/District Data)
// import { indiaData } from '../constants/indiaData';
// Now the states and districts are fetched dynamically from the CountriesNow API.
import { profileTranslations } from '../constants/profileTranslations';
import { IconArrowLeft, IconShoppingBag, IconSeeding } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

const InputGroup = ({ label, children, error }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', width: '100%' }}>
        <Typography sx={{
            color: colors.text_dark,
            fontSize: sizes.formLabel || '0.875rem',
            fontWeight: 500,
            lineHeight: 1,
            fontFamily: fonts.poppins
        }}>
            {label}
        </Typography>
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {children}
            {error && (
                <Typography sx={{
                    color: '#FF5630',
                    fontSize: '12px',
                    fontWeight: 500,
                    fontFamily: fonts.poppins,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    mt: '2px'
                }}>
                    ⚠ {error}
                </Typography>
            )}
        </Box>
    </Box>
);

const ProfileScreen = ({ formData, onChange, onSubmit, onBack, lang, onLangChange, role, errors = {}, isEditMode = false }) => {
    const localT = profileTranslations[lang] || profileTranslations['en'];
    const { t } = useTranslation('common');

    // API States
    const [statesList, setStatesList] = React.useState([]);
    const [districtsList, setDistrictsList] = React.useState([]);

    // Translate lists for dropdowns
    const translatedStates = statesList.map(state => ({
        value: state,
        label: t(`states.${state}`, state)
    }));

    const translatedDistricts = districtsList.map(district => ({
        value: district,
        label: t(`districts.${district}`, district)
    }));

    React.useEffect(() => {
        const fetchStates = async () => {
            try {
                const response = await fetch("https://countriesnow.space/api/v0.1/countries/states", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ country: "India" })
                });
                const data = await response.json();
                if (!data.error && data.data && data.data.states) {
                    const states = data.data.states.map(state => state.name);
                    setStatesList(states);
                }
            } catch (error) {
                console.error("Error fetching states:", error);
            }
        };
        fetchStates();
    }, []);

    React.useEffect(() => {
        const fetchDistricts = async () => {
            if (!formData.state) {
                setDistrictsList([]);
                return;
            }
            try {
                const response = await fetch("https://countriesnow.space/api/v0.1/countries/state/cities", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ country: "India", state: formData.state })
                });
                const data = await response.json();
                if (!data.error && data.data) {
                    setDistrictsList(data.data);
                } else {
                    setDistrictsList([]);
                }
            } catch (error) {
                console.error("Error fetching districts:", error);
                setDistrictsList([]);
            }
        };
        fetchDistricts();
    }, [formData.state]);

    // Custom logic to adapt the role string if it differs
    const displayRole = role === 'Seller' ? 'Farmer' : (role || 'User');
    const subtitle = localT.subtitle.replace("Farmer", (localT[displayRole.toLowerCase()] || displayRole));

    const isFormValid = Boolean(
        formData.firstName?.trim() &&
        formData.lastName?.trim() &&
        formData.gender &&
        formData.age &&
        formData.phoneNumber?.length === 10 &&
        formData.state &&
        formData.district &&
        formData.tahsil &&
        formData.village &&
        (formData.role || role)
    );

    return (
        <Box sx={{
            minHeight: isEditMode ? 'auto' : '100vh',
            backgroundColor: colors.background_light,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: '1rem', sm: '1.5rem' }, // Responsive padding rems
            fontFamily: fonts.poppins,
            width: '100%' // percentage width
        }}>
            <Box sx={{
                width: '100%',
                maxWidth: '600px', // max width
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
            }}>

                {/* Header Section - Hide in Edit Mode */}
                {!isEditMode && (
                    <Box sx={{
                        position: 'relative',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        mb: '1.5rem' // 24px rem
                    }}>
                        <Box
                            onClick={onBack}
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem', // 8px rem
                                cursor: 'pointer',
                                p: 0
                            }}
                        >
                            {/* Using Tabler Back Arrow exactly matching specs */}
                            <IconArrowLeft
                                color={colors.text}
                                size={16}
                                stroke={1.5}
                                style={{ top: '0.25rem', marginTop: '0.25rem' }}
                            />
                            <Typography sx={{
                                color: colors.text,
                                fontSize: sizes.body || '1rem',
                                fontWeight: 400,
                                fontFamily: fonts.poppins,
                                lineHeight: 1.5,
                                mt: 0
                            }}>
                                {localT.back}
                            </Typography>
                        </Box>

                        <Box sx={{
                            width: '4rem', // 64px rem
                            height: '4rem',
                            borderRadius: '2rem', // 32px
                            backgroundColor: colors.role_selected_bg,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mt: '2rem', // 32px rem
                            mb: '1rem' // 16px rem
                        }}>
                            {/* Dynamic Icon circle using Tabler Icons */}
                            {(role || formData.role) === 'Buyer' ? (
                                <IconShoppingBag color={colors.primary} size={32} stroke={1.5} />
                            ) : (
                                <IconSeeding color={colors.primary} size={32} stroke={1.5} />
                            )}
                        </Box>

                        <Typography sx={{
                            color: colors.text_dark,
                            fontSize: sizes.h1_role || '1.5rem',
                            fontWeight: 700,
                            lineHeight: 1.3,
                            letterSpacing: '-0.0375rem',
                            mb: '0.25rem', // 4px rem
                            fontFamily: fonts.poppins
                        }}>
                            {localT.title}
                        </Typography>
                        <Typography sx={{
                            color: colors.text,
                            fontSize: sizes.body || '1rem',
                            fontWeight: 400,
                            lineHeight: 1.5,
                            m: 0,
                            fontFamily: fonts.poppins
                        }}>
                            {subtitle}
                        </Typography>
                    </Box>
                )}

                <Box sx={{
                    width: '100%',
                    background: colors.light,
                    border: `1px solid ${errors.role ? '#FF5630' : colors.border}`,
                    borderRadius: '2rem', // 32px rem
                    padding: { xs: '1.5rem 1rem', sm: '2rem 1.5rem' }, // Responsive padding rems
                    boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.04), 0px 4px 20px -4px rgba(0, 0, 0, 0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1.25rem' // 20px rem
                }}>
                    {errors.role && (
                        <Typography sx={{
                            color: '#FF5630',
                            fontSize: '14px',
                            fontWeight: 600,
                            fontFamily: fonts.poppins,
                            textAlign: 'center',
                            mb: '0.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            backgroundColor: 'rgba(255, 86, 48, 0.08)',
                            padding: '10px',
                            borderRadius: '12px'
                        }}>
                            ⚠ {errors.role}
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: '1rem', width: '100%' }}>
                        <InputGroup label={localT.firstName} error={errors.firstName}>
                            <FormInput
                                name="firstName"
                                value={formData.firstName || ''}
                                placeholder={localT.firstNamePlaceholder}
                                onChange={onChange}
                            />
                        </InputGroup>
                        <InputGroup label={localT.lastName} error={errors.lastName}>
                            <FormInput
                                name="lastName"
                                value={formData.lastName || ''}
                                placeholder={localT.lastNamePlaceholder}
                                onChange={onChange}
                            />
                        </InputGroup>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: '1rem', width: '100%' }}>
                        <InputGroup label={localT.gender} error={errors.gender}>
                            <Box
                                component="select"
                                name="gender"
                                value={formData.gender || ''}
                                onChange={onChange}
                                sx={{
                                    boxSizing: 'border-box',
                                    width: '100%',
                                    height: '48px',
                                    borderRadius: radius.lg,
                                    border: `2px solid ${colors.border}`,
                                    backgroundColor: colors.background_light,
                                    padding: `0 ${spacing.xl}`,
                                    fontFamily: fonts.poppins,
                                    fontSize: sizes.small || '14px',
                                    color: colors.text_dark,
                                    outline: 'none',
                                    appearance: 'none',
                                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'right 1rem center',
                                    backgroundSize: '1em',
                                    cursor: 'pointer',
                                    '&:focus': { borderColor: colors.primary }
                                }}
                            >
                                <option value="" disabled>{localT.genderPlaceholder}</option>
                                <option value="Male">{localT.male}</option>
                                <option value="Female">{localT.female}</option>
                                <option value="Other">{localT.other}</option>
                            </Box>
                        </InputGroup>
                        <InputGroup label={localT.age} error={errors.age}>
                            <FormInput
                                name="age"
                                type="number"
                                value={formData.age || ''}
                                placeholder={localT.agePlaceholder}
                                onChange={onChange}
                            />
                        </InputGroup>
                    </Box>

                    <InputGroup label={localT.mobile} error={errors.phoneNumber}>
                        <FormInput
                            name="phoneNumber"
                            value={formData.phoneNumber || ''}
                            placeholder={localT.mobilePlaceholder}
                            onChange={onChange}
                        />
                    </InputGroup>

                    <InputGroup label={localT.email}>
                        <FormInput
                            name="email"
                            value={formData.email || ''}
                            readOnly
                        />
                    </InputGroup>

                    <InputGroup label={localT.role}>
                        <FormInput
                            name="role"
                            value={role || formData.role ? (localT[(role || formData.role).toLowerCase()] || (role || formData.role)) : ''}
                            readOnly
                        />
                    </InputGroup>

                    <InputGroup label={localT.state} error={errors.state}>
                        <SearchableDropdown
                            name="state"
                            value={formData.state || ''}
                            options={translatedStates}
                            placeholder={localT.statePlaceholder}
                            onChange={(e) => {
                                onChange(e);
                                // Also clear district when state changes
                                onChange({ target: { name: 'district', value: '' } });
                            }}
                            searchText={localT.searchPlaceholder}
                            noResultsText={localT.noResults}
                        />
                    </InputGroup>

                    <InputGroup label={localT.district} error={errors.district}>
                        <SearchableDropdown
                            name="district"
                            value={formData.district || ''}
                            options={translatedDistricts}
                            placeholder={localT.districtPlaceholder}
                            onChange={onChange}
                            disabled={!formData.state}
                            searchText={localT.searchPlaceholder}
                            noResultsText={localT.noResults}
                        />
                    </InputGroup>

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: '1rem' /* 16px rem */, width: '100%' }}>
                        <InputGroup label={localT.taluka} error={errors.tahsil}>
                            <FormInput
                                name="tahsil"
                                value={formData.tahsil || ''}
                                placeholder={localT.talukaPlaceholder}
                                onChange={onChange}
                            />
                        </InputGroup>
                        <InputGroup label={localT.village} error={errors.village}>
                            <FormInput
                                name="village"
                                value={formData.village || ''}
                                placeholder={localT.villagePlaceholder}
                                onChange={onChange}
                            />
                        </InputGroup>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' /* 12px rem */, mt: '0.25rem' /* 4px rem */ }}>
                        <Typography sx={{ color: colors.text_dark, fontSize: sizes.formLabel || '0.875rem', fontWeight: 500, lineHeight: 1, fontFamily: fonts.poppins }}>
                            {localT.prefLang}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: '0.5rem' /* 8px rem */, width: '100%' }}>
                            {['en', 'hi', 'mr'].map((l) => (
                                <Box
                                    component="button"
                                    key={l}
                                    onClick={() => onLangChange(l)}
                                    sx={{
                                        flex: 1, height: '2.25rem', borderRadius: '1.125rem', border: 'none',   // 36px/18px rems
                                        backgroundColor: lang === l ? colors.primary : colors.tag_bg,
                                        color: lang === l ? colors.light : colors.text,
                                        fontFamily: fonts.poppins, fontSize: sizes.small || '0.875rem',
                                        fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s'
                                    }}
                                >
                                    {l === 'en' ? localT.langEn : l === 'hi' ? localT.langHi : l === 'mr' ? localT.langMr : l}
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Box
                        component="button"
                        onClick={onSubmit}
                        sx={{
                            width: '100%',
                            height: '3rem', // 48px -> 3rem
                            borderRadius: '1.5rem', // 24px -> 1.5rem
                            border: 'none',
                            backgroundColor: isFormValid ? colors.primary : '#E0E0E0',
                            color: isFormValid ? colors.light : '#9E9E9E',
                            fontFamily: fonts.poppins,
                            fontSize: sizes.body || '1rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            mt: '0.5rem',
                            opacity: 1,
                            boxShadow: isFormValid ? '0 8px 24px -4px rgba(0, 167, 111, 0.24)' : 'none',
                            '&:hover': isFormValid ? {
                                backgroundColor: colors.primary_dark || colors.primary,
                                transform: 'translateY(-2px)',
                                boxShadow: '0 12px 32px -4px rgba(0, 167, 111, 0.32)'
                            } : {}
                        }}
                    >
                        {isEditMode ? t('common:save', 'Save Changes') : localT.createAccount}
                    </Box>
                </Box>

                {!isEditMode && (
                    <Box sx={{ mt: '1rem', display: 'flex', gap: '0.5rem', color: colors.text_light }}>
                         {/* ... maybe some footer legal stuff later ... */}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ProfileScreen;
